import { create } from 'zustand';
import { getApiErrorMessage } from '../lib/errors';

export type NotificationTone = 'success' | 'info' | 'error';

export interface NotificationAction {
  label: string;
  /** Debe ejecutar la reversa real en el servidor, no sólo ocultar la UI. */
  onClick: () => Promise<void> | void;
}

export interface AppNotification {
  id: string;
  message: string;
  tone: NotificationTone;
  duration: number;
  action?: NotificationAction;
  isActing: boolean;
  error?: string;
}

export interface ShowNotification {
  message: string;
  tone?: NotificationTone;
  /** Milisegundos visibles; los undo duran más para dar tiempo a reaccionar. */
  duration?: number;
  action?: NotificationAction;
}

interface NotificationState {
  notifications: AppNotification[];
  show: (input: ShowNotification) => string;
  dismiss: (id: string) => void;
  runAction: (id: string) => Promise<void>;
}

let sequence = 0;

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  show: ({ message, tone = 'success', duration, action }) => {
    const id = `notice-${Date.now()}-${sequence++}`;
    const notification: AppNotification = {
      id,
      message,
      tone,
      duration: duration ?? (action ? 15_000 : 6_000),
      action,
      isActing: false,
    };
    // Un máximo de tres evita que una serie de registros tape el contenido.
    set((state) => ({ notifications: [...state.notifications, notification].slice(-3) }));
    return id;
  },
  dismiss: (id) =>
    set((state) => ({
      notifications: state.notifications.filter((notification) => notification.id !== id),
    })),
  runAction: async (id) => {
    const notification = get().notifications.find((item) => item.id === id);
    if (!notification?.action || notification.isActing) return;
    set((state) => ({
      notifications: state.notifications.map((item) =>
        item.id === id ? { ...item, isActing: true, error: undefined } : item,
      ),
    }));
    try {
      await notification.action.onClick();
      get().dismiss(id);
    } catch (error) {
      const message = getApiErrorMessage(error, 'No se pudo deshacer. Inténtalo de nuevo.');
      set((state) => ({
        notifications: state.notifications.map((item) =>
          item.id === id ? { ...item, isActing: false, error: message } : item,
        ),
      }));
    }
  },
}));

/** API imperativa para usar el mismo gestor desde hooks de React Query. */
export const notify = {
  success: (message: string, options: Omit<ShowNotification, 'message' | 'tone'> = {}) =>
    useNotificationStore.getState().show({ message, tone: 'success', ...options }),
  info: (message: string, options: Omit<ShowNotification, 'message' | 'tone'> = {}) =>
    useNotificationStore.getState().show({ message, tone: 'info', ...options }),
  error: (message: string, options: Omit<ShowNotification, 'message' | 'tone'> = {}) =>
    useNotificationStore.getState().show({ message, tone: 'error', ...options }),
};
